import { useEffect, useState } from 'react';
import { AppState, Platform } from 'react-native';

import { networkService } from '../services/network';

import type { AppStateStatus } from 'react-native';

/**
 * `background` is the only state that stops a poller (#1069 Story 2.3, D7,
 * as narrowed by the 2026-09-11 code review). `inactive` is transient on
 * iOS: the app switcher, Control Centre, an incoming-call banner and every
 * system permission alert, including the ones the import flow itself
 * raises, and `useJobStatus` puts this gate in `enabled`, so stopping on
 * `inactive` switched the transcription poller OFF for the length of a
 * permission prompt. An UNRESOLVED state (`null` before the native constants
 * are read, or the type's `'unknown'`) reads as foreground, because a poller
 * that never starts while the state is unresolved is a silent defect and the
 * rule that matters is that going to the background stops it.
 */
const isForeground = (state: AppStateStatus | string | null | undefined) =>
  state !== 'background';

/**
 * ⚠️ `AppState` CAN BE ABSENT. The client Jest project's global `react-native`
 * mock (`src/setupTests.ts`) defines no `AppState`, and every suite that
 * renders `useJobStatus` or the correspondence summary query runs through
 * this hook under it. Absence reads as "always foreground" (which is also
 * what the web branch means) rather than a `TypeError` on mount.
 */
const hasAppState = (): boolean =>
  typeof (AppState as { addEventListener?: unknown } | undefined)
    ?.addEventListener === 'function';

/**
 * Whether a poller should be running right now: online, and in the
 * foreground.
 *
 *   · Web: the tab is visible (`document.visibilityState`).
 *   · iOS / Android (Story 1069-2.3, D7): the app is not `background` per
 *     `AppState`. Until 2.3 the hook had no native half, so a React Query
 *     interval gated on it kept firing in the background on a device: React
 *     Query pauses an interval on `focusManager.isFocused()`, which browser
 *     `visibilitychange` alone drives, and nothing in `src/` wires
 *     `AppState` into it.
 *
 * ⚠️ TWO CONSUMERS, AND BOTH NOW STOP IN THE BACKGROUND ON NATIVE:
 * `useJobStatus` (the transcription poller, which previously ran on in the
 * background on a device and now matches its own web behaviour; note that it
 * wires this into `enabled`, so its query is switched off, not merely left
 * unpolled) and `useCorrespondenceSummary` (the discussion panel's 10 s
 * clock). A bounded side effect, stated here and pinned twice: the gate
 * itself in `usePollingEnabled.test.ts`, and that the transcription poller
 * is off while the gate is off in `useJobStatus.test.tsx`.
 *
 * ⚠️ NOT the app-wide `focusManager` wiring. That would make every
 * `refetchOnWindowFocus: true` query in the app refetch on foreground, a
 * product decision about data and battery that no poller story owns (Story
 * 2.2 D7, `deferred-work.md`). This is the bounded version: pollers reading
 * this hook stop, nothing else changes.
 *
 * @returns true if polling should be active, false otherwise
 */
export const usePollingEnabled = (): boolean => {
  const [isOnline, setIsOnline] = useState(networkService.isConnected());
  const [isVisible, setIsVisible] = useState(true);
  const [isActive, setIsActive] = useState(() =>
    Platform.OS === 'web' || !hasAppState()
      ? true
      : isForeground(AppState.currentState),
  );

  // Listen for network changes
  useEffect(() => {
    const cleanup = networkService.addListener(connected => {
      setIsOnline(connected);
    });
    return cleanup;
  }, []);

  // Listen for visibility changes (web only)
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const handleVisibilityChange = () => {
        setIsVisible(document.visibilityState === 'visible');
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Set initial visibility state
      setIsVisible(document.visibilityState === 'visible');

      return () => {
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange,
        );
      };
    }
    return undefined;
  }, []);

  // Listen for app-state changes (native only; D7)
  useEffect(() => {
    if (Platform.OS === 'web' || !hasAppState()) return undefined;
    const subscription = AppState.addEventListener('change', state => {
      setIsActive(isForeground(state));
    });
    // Re-read on subscribe: the app may have resumed between the initial
    // render and this effect, and that change has already been delivered.
    setIsActive(isForeground(AppState.currentState));
    return () => {
      // `hasAppState` proves only that `addEventListener` is a function; a
      // partial runtime or mock that returns nothing must not throw on the
      // unmount of every screen that polls (code review 2026-09-11).
      subscription?.remove?.();
    };
  }, []);

  // Only enable polling if online, visible (web) and active (native)
  return isOnline && isVisible && isActive;
};
